// File: src/pages/ExpertChatPage.jsx
// -----------------------------------------------------------------------------
// Page "chat expert" – affichage d'un expert (avatar, titre, description) +
// suggestions de questions rapides, zone de saisie partagée
// -----------------------------------------------------------------------------
import React, { useContext, useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import MessageFeed from '../components/MessageFeed';
import ChatInput from "../components/ChatInput";
import PremiumModal from '../components/premium/PremiumModal';
import { ConversationsContext } from "../context/ConversationsContext";
import { getItem } from "../api/jobExplorer";
import { ProsContext } from "../context/ProsContext";
import { HistoryContext } from "../context/HistoryContext";
import { useFirstname } from "../hooks/useFirstname";
import { agentConfig } from "../config/agentConfig";
import { AuthContext } from "../context/AuthContext";
import { vfUpdateVariables } from "../api/VoiceflowInteractions";

import vfkbToJson from "../hooks/vfkbToJson";
import { ArrowLeft } from "lucide-react";

export default function ExpertChatPage({onSend}) {
  const { user, updateUser } = useContext(AuthContext);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [shouldLaunchAction, setShouldLaunchAction] = useState(false);
  const [focus, setFocus] = useState(0);
  const { getRandomFirstname } = useFirstname();
  const { chatId, gender } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addAction, history } = useContext(HistoryContext);
  const { itemsPro, setItemsPro } = useContext(ProsContext);
  const { convs, customAction, sendToVF, resetCustomAction, isWaitingForResponse, isWaitingForFeedback, setWaitingForFeedbackValues, askForFeedback } = useContext(ConversationsContext);
  const [expert, setExpert] = useState({});
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumModalReason, setPremiumModalReason] = useState('');

  const navigate = useNavigate();
  const luxInitRef = useRef(false);

  let conv = {}
  if ( chatId === 'lux' )
    conv = convs[`${chatId}`] || {sessionSlug: '', messages: [], bot: {}};
  else
    conv = convs[`${chatId}/${gender}`] || {sessionSlug: '', messages: [], bot: {}};

  useEffect(() => {
    if ( customAction.event == "redirect") {
      setShouldLaunchAction(true)
    }
  }, [customAction])

  // Enregistrer dans l'historique et mettre à jour les variables LUX quand on arrive sur la conversation LUX
  useEffect(() => {
    if (chatId !== 'lux') {
      luxInitRef.current = false;
      return;
    }

    if (luxInitRef.current) return;
    luxInitRef.current = true;

    addAction({ type: 'LUX_CONVERSATION', values: {} });

    // Mettre à jour la variable history de LUX
    if (user?.id && history) {
      const luxSessionId = `lux_${user.id}`;
      const historyContent = history.map(item => item.content).join("\n");
      vfUpdateVariables(
        luxSessionId,
        { history: historyContent, status_compte: user.is_premium ? 'premium' : 'freemium' },
        process.env.REACT_APP_VF_LUX_API_KEY
      ).catch(err => console.error('Erreur mise à jour history LUX:', err));
    }
  }, [chatId])

  async function launchAction() {
    const customActionJson = JSON.parse(customAction.payload)
    let job = vfkbToJson(customActionJson.content)
    if (  !user.is_premium &&
          Object.keys(itemsPro).length >= 2 &&
          itemsPro[job.id] === undefined &&
          customActionJson !== "back_from_expert") {
      const jobData = await getItem(job.id)
      const jobTitle = customActionJson.gender === 'boy' ? jobData.title_m : jobData.title_f
      setPremiumModalReason(`échanger avec ${jobTitle}`)
      setShowPremiumModal(true)
      return
    }
    setTimeout(() => {
      (async () => {
        try {
          const customActionJson = JSON.parse(customAction.payload)
          if ( customActionJson.action === "back_from_expert") {
            await addAction({type: "BACK_FROM_EXPERT", values: customActionJson})
            setWaitingForFeedbackValues({title: customActionJson.expert_job, name: customActionJson.expert_name})
            navigate("/chat/lux");
          } else {
            let speciality = customActionJson.speciality
            let job = vfkbToJson(customActionJson.content)
            const job_from_api = await getItem(job.id)

            let gender = customActionJson.gender
            let name
            if ( itemsPro[job_from_api.id] ) {
              name = itemsPro[job_from_api.id].name
              gender = itemsPro[job_from_api.id].gender
            } else {
              name = gender === 'boy' ? getRandomFirstname('male') : getRandomFirstname('female')
              setItemsPro({
                ciaov2_job_id: job_from_api.id,
                name,
                gender,
                title: gender === 'boy' ? job_from_api.title_m : job_from_api.title_f
              })
            }
            const qualities = job_from_api._qualities.map( (quality) => `${quality._quality.label} : ${quality.explanation}` ).join("\n")
            const level_of_education = job_from_api._study_levels.map( (study_level) => study_level._study_level.label ).join(", ")
            const salary = `Salaire début de carrière de ${job_from_api.salary_min}€ pouvant aller jusqu'à ${job_from_api.salary_max}€. ${job_from_api.salary_explanation}`
            const salary_evolution_explanation = job_from_api.salary_evolutions.map( (salary_evolution) => 
              `${salary_evolution.experience} (${salary_evolution.experience_years}) gagne environ ${salary_evolution.experience_salary}. Role : ${salary_evolution.experience_role}.`
            ).join("\n")
            const values = {
              id: job_from_api.id,
              title: gender == 'boy' ? job_from_api.title_m : job_from_api.title_f,
              title_for_prompt: gender == 'boy' ? job_from_api.title_for_prompt_m : job_from_api.title_for_prompt_f,
              name,
              picture: gender == 'boy' ? job_from_api.aws_pictures_m[1] : job_from_api.aws_pictures_f[1],
              qualities: qualities,
              salary: salary,
              salary_evolution_explanation: salary_evolution_explanation,
              level_of_education: level_of_education,
              gender: gender,
              speciality,
              from: 'lux'
            }
            await addAction({type: "LUX_TO_EXPERT", values})
            setWaitingForFeedbackValues({title: values.title, name: values.name})
            let path = await onSend(values)
            navigate(path);
          }
          resetCustomAction()
        } catch (err) {
          console.error("initSession failed:", err);
        }
      })();
    }, 1000);
  }

  useEffect(() => {
    if ( isWaitingForFeedback && (chatId === 'lux' || chatId === 'dip') ) {
      (async () => {
        setChatEnabled(false)
        await askForFeedback()
      })()
    }
    if (expert.id === chatId && expert.gender === gender) return;
    setFocus( focus + 1 )
  }, [chatId]);

  useEffect(() => {
    if (expert.id === undefined && conv['sessionSlug'] != '') {
      const agentId = ( chatId === 'lux' || chatId === 'dip') ? chatId : 'expert'
      setExpert(agentConfig[agentId].getBotInfos(user, conv.bot))
    }
  }, [conv]);

  // Auto-send question from URL param
  useEffect(() => {
    const question = searchParams.get('q');
    if (question && conv.sessionSlug && chatEnabled) {
      setTimeout(() => {
        handleSend(question);
        setSearchParams({}, { replace: true });
      }, 1500)
    }
  }, [conv.sessionSlug, searchParams]);

  function handleSend(message) {
    if (chatId === 'lux' && !user?.is_premium) {
      const userMsgCount = (conv.messages || []).filter(m => m.from === 'user').length;
      if (userMsgCount >= 800) {
        setPremiumModalReason('continuer à discuter avec LUX');
        setShowPremiumModal(true);
        return;
      }
    }
    setChatEnabled(false)
    setFocus(focus+1)
    if ( chatId === 'lux' )
      sendToVF(chatId, expert.gender, { type: 'text', payload: message }, process.env.REACT_APP_VF_LUX_API_KEY);
    else
      sendToVF(chatId, gender, { type: 'text', payload: message }, process.env.REACT_APP_VF_API_KEY);
  }

  function shouldEnableChat() {
    setChatEnabled(true)
    if (shouldLaunchAction) {
      launchAction();
      setShouldLaunchAction(false)
    }
  }

  const gotoJobPage = async () => {
    navigate(`/pro/${chatId}/${expert.gender}`)
  }

  return (
    <div className="asolute flex flex-1 flex-col h-dvh w-full items-center py-6 px-4 no-scrollbar">

      {chatId !== 'lux' && (<div className="max-w-3xl mb-3 md:pl-6 pl-12 mt-1 flex cursor-pointer w-full" onClick={() => navigate(`/pro/${chatId}/${gender}`)}>
        <div className="flex items-center gap-2 cursor-pointer">
          <ArrowLeft className="w-4 h-4" />
          <div className="text-sm">Tableau de bord</div>
        </div>
      </div>)}
      <MessageFeed key={`messageFeed${chatId}${gender}`} gotoJobPage={gotoJobPage} expert={expert} messages={conv.messages} isAwaitingResponse={isWaitingForResponse} chatId={chatId} enableChat={shouldEnableChat} />
      {/* ChatInput réutilisé */}
      <div className="w-full max-w-3xl px-4 sticky items-center">
        <ChatInput placeholder={"Pose une question"} onSend={handleSend} focus={focus} chatEnabled={chatEnabled}/>
      </div>

      <PremiumModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        askedForPremium={user?.asked_for_premium}
        onUpdateUser={updateUser}
        context="Tu as déjà rencontré 2 professionnels"
        reason={premiumModalReason}
      />
    </div>
  );
}
